import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useAdminAuth, hasRole } from '../../auth';
import { Card } from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import StatusPill from '../../components/StatusPill';
import { ConfirmDialog } from '../../components/Dialog';
import { formatRelative } from '../../format';

/**
 * System › Flags — kill switches.
 *
 * One change from the old page, and it matters: a flag toggle now goes
 * through a confirmation rather than firing on the click. These are kill
 * switches. The whole point of the mechanism is that flipping one changes
 * live behaviour immediately for everyone, which is exactly the kind of
 * action that should not be one stray click away in a list — particularly
 * since the badge that toggled it looked like a status indicator rather
 * than a button.
 */
const input = 'rounded-lg border border-hairline px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300';

export default function FlagsPage() {
  const { admin } = useAdminAuth();
  const canEdit = hasRole(admin, 'operator', 'owner');

  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/ops/flags');
      setFlags(data.items || []);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load flags'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    const flag = pending;
    try {
      const { data } = await adminApi.patch(`/ops/flags/${encodeURIComponent(flag.flag_key)}`, {
        enabled: !flag.enabled,
      });
      setFlags((prev) => prev.map((f) => (f.flag_key === flag.flag_key ? data.after : f)));
      toast.success(`${flag.flag_key} ${data.after.enabled ? 'enabled' : 'disabled'}`);
      setPending(null);
    } catch (err) {
      toast.error(errMsg(err, 'Could not toggle flag'));
      setPending(null);
    }
  };

  const createFlag = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await adminApi.post('/ops/flags', { flag_key: newKey, description: newDesc });
      setNewKey('');
      setNewDesc('');
      await load();
      toast.success('Flag created');
    } catch (err) {
      toast.error(errMsg(err, 'Could not create flag'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Flags"
        hint="Kill switches. A change takes effect on the next request MAHI serves — there is no cache to wait out."
      />

      {canEdit && (
        <Card title="New flag" className="mb-6">
          <form onSubmit={createFlag} className="flex flex-wrap gap-2">
            <input
              required
              placeholder="flag_key (lowercase, dots or underscores)"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              className={`min-w-[220px] flex-1 ${input}`}
            />
            <input
              required
              placeholder="What does turning this off do?"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              className={`min-w-[260px] flex-[2] ${input}`}
            />
            <button
              type="submit"
              disabled={creating}
              className="rounded-lg bg-raspberry-600 px-4 py-2 text-sm font-medium text-white hover:bg-raspberry-700 disabled:opacity-50"
            >
              {creating ? 'Creating…' : 'Create flag'}
            </button>
          </form>
        </Card>
      )}

      {loading ? (
        <Card><p className="text-sm text-ink-muted">Loading…</p></Card>
      ) : flags.length === 0 ? (
        <EmptyState
          title="No flags yet"
          detail="Create one here, then have MAHI read it. A flag nothing reads is inert."
        />
      ) : (
        <div className="divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-surface shadow-sm">
          {flags.map((f) => (
            <div key={f.flag_key} className="flex items-center justify-between gap-4 px-4 py-3.5">
              <div className="min-w-0">
                <div className="font-mono text-sm font-medium text-ink">{f.flag_key}</div>
                <div className="text-xs text-ink-secondary">{f.description}</div>
                {f.updated_by && (
                  <div className="mt-0.5 text-[11px] text-ink-muted">
                    Last changed by {f.updated_by} · {formatRelative(f.updated_at)}
                  </div>
                )}
              </div>
              <div className="flex flex-shrink-0 items-center gap-3">
                <StatusPill
                  tone={f.enabled ? 'good' : 'neutral'}
                  label={f.enabled ? 'Enabled' : 'Disabled'}
                />
                {canEdit && (
                  <button
                    onClick={() => setPending(f)}
                    className="rounded-md border border-hairline px-2.5 py-1 text-[11px] font-medium text-ink-secondary hover:bg-surface-page hover:text-ink"
                  >
                    {f.enabled ? 'Disable' : 'Enable'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pending)}
        tone={pending?.enabled ? 'critical' : 'default'}
        title={pending?.enabled
          ? `Disable ${pending?.flag_key}?`
          : `Enable ${pending?.flag_key}?`}
        body={
          <>
            <p>{pending?.description}</p>
            <p className="mt-2">
              This takes effect on the next request MAHI serves. The change is recorded in the audit log
              with your name, the old value and the new one.
            </p>
          </>
        }
        confirmLabel={pending?.enabled ? 'Disable flag' : 'Enable flag'}
        onConfirm={toggle}
        onClose={() => setPending(null)}
      />
    </div>
  );
}
